import React, { useEffect, useState } from "react";
import useAuth from '../../../hooks/useAuth';
import Select from 'react-select';
import GearSpinner from '../../../components/GearSpinner/GearSpinner';
import Loader from '../../../components/Loader/Loader';
import logo from '../../../assets/images/big-logo.jpg';
import printIcon from '../../../assets/images/print-img.png';
import shareIcon from '../../../assets/images/share-img.png';
import pdfIcon from '../../../assets/images/pdf-img.png';
import saveIcon from '../../../assets/images/savedraft-icon.png';
import UserServices from "../../../services/UserServices";
import QuoteMeService from "../../../services/QuoteMeService";
import QuoteCustomerSalesTable from "../QuoteCustomerSalesTable";
import AlertMessage from "../../../components/AlertMessage/AlertMessage";
import moment from "moment-timezone";
import ShareQuoteMeModal from '../../../components/Modals/ShareQuoteMeModal';
import '../print.css';
import { formatUSD } from '../../../utils/common';
import { Validation } from "../../../utils/validation";

const QuoteCustomerTab = ({ active }) => {
    const { user, updateCustomers } = useAuth();
    const [customerLoaded, setCustomerLoaded] = useState(false);
    const [showSpinner, setShowSpinner] = useState(true);
    const [showLoader, setShowLoader] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [sameAsBilling, setSameAsBilling] = useState(false);
    const [products,setProducts] = useState([]);
    const [productMap, setProductMap] = useState({});
    const [multiplierMap, setMultiplierMap] = useState({});
    const [isProducts, setIsProducts] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [estTime, setEstTime] = useState("");
    const [showShareModal, setShowShareModal] = useState(false);
    const [rowErrors, setRowErrors] = useState({});

    
    const [formData, setFormData] = useState({});
    const userServices = new UserServices();
    const quoteMeService = new QuoteMeService();

    const getNextQuoteOrderNumber = () => {
        quoteMeService.getNextQuoteOrderNumber().then(response => {
            const data = response.data;
            if(data.status === 'success'){
                setFormData(prev => ({
                    ...prev,
                    order_number: data.result.order_number
                }))
            }
        }).catch(error => {
            console.error('Error fetching next quote order number:', error);
        });
    }

    useEffect(() => {
        setEstTime(
            moment()
            .tz("America/New_York")
            .format("MMMM DD, YYYY hh:mm:ss A z")
        );
    }, []);

    useEffect(() => {
        setFormData(
            {
                "customer_name": null,
                "rep":null,
                "payment_terms":null,
                "fob_point":"Origin",
                "carrier":null,
                "ship_service":null,
                "date_schedule":null,
                "billing_address": null,
                "shipping_address": null,
                "order_date": moment().format('YYYY-MM-DD'),
                "sub_total":"0.00",
                "sales_tax":"0.00",
                "total":"0.00",
                "notes":null,
                "sales_data":[{
                    "type":"Sale",
                    "product_number":"",
                    "description":"",
                    "unit_price":"0.00",
                    "quantity":"",
                    "total_price":"0.00"
                }],
        });
        setSameAsBilling(false);
        setIsProducts(false);
        getNextQuoteOrderNumber();
    }, [selectedCustomer]);
    
    useEffect(() => {
        if(user && !customerLoaded) {
			setCustomerLoaded(true);
			userServices.getCustomerdata({portal_user_id: user.id, portal_user_name: user.name, customer_from: 'fishbowl'}).then(response => {
				const data = response.data;
				if(data.status === 'success') {
					const mappedCustomers = data.result.map(c => ({
						value: c.id,
						label: c.name,
					}));
					setCustomers(mappedCustomers);
					if (mappedCustomers.length > 0) {
						updateCustomers(mappedCustomers);
					}
				}
				setShowSpinner(false);
			}).catch(error => {
                console.error('Error fetching next quote order number:', error);
            });
        }
    },[user, customerLoaded]);

    useEffect(()=> {
        if (!selectedCustomer || !selectedCustomer.label) return;
        setShowSpinner(true);
        quoteMeService.getCustomerMultipliers({
            "portal_user_name":user.name,
            "company_name": selectedCustomer.label
        }).then(response => {
            setShowSpinner(false);
            const data = response.data;
            if (data.status === "success") {

                // 1️⃣ Build multiplier map
                const mMap = {};
                data.result.customers.multipliers.forEach(m => {
                    mMap[m.multiplier_group] = Number(m.customer_multiplier);
                });
                setMultiplierMap(mMap);

                // 2️⃣ Build product map (num → product)
                const rawProducts = Array.isArray(data.result.products)
                    ? data.result.products
                    : Object.values(data.result.products).flat();

                const pMap = {};
                const productOptions = [];

                rawProducts.forEach(p => {
                    pMap[p.num] = p;
                    productOptions.push({
                        value: p.num,
                        label: p.num
                    });
                });

                if(rawProducts.length > 0) {
                    setIsProducts(true);
                }

                setProductMap(pMap);
                setProducts(productOptions);

                setFormData(prev => ({
                    ...prev,
                    portal_user_id: user.id,
                    rep: data.result.customers.initials,
                    customer_name: data.result.customers.customer_name,
                    billing_address: `${data.result.customers.address}, ${data.result.customers.city}, ${data.result.customers.state_name}, ${data.result.customers.zip}`,
                    payment_terms: data.result.customers.payment_terms
                }));
            }

            //console.log(response);
        }).catch(error => {
            console.error('Error fetching next quote order number:', error);
        });
        
    },[selectedCustomer]);
    
    if (!active) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]:value
        }));
    }


    const handleSameAsBilling = (e) => {
        const checked = e.target.checked;
        setSameAsBilling(checked);

        if (checked) {
            setFormData(prev => ({
                ...prev,
                shipping_address: prev.billing_address
            }));
        } else {

            setFormData(prev => ({
                ...prev,
                shipping_address: null
            }));
        }
    };

    const handleShareClick = async () => {
        setShowShareModal(true);
    };

    const validateSalesRows = () => {
        let currentErrors = {};
        formData.sales_data.forEach((row, index) => {
            const errors = Validation(row, ['product_number', 'quantity']);
            
            if (errors && Object.keys(errors).length > 0) {
                currentErrors[index] = errors;
            }
        });
        setRowErrors(currentErrors);
        return Object.keys(currentErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateSalesRows()) return;

        setShowLoader(true);
        await saveQuote({ resetAfterSave: true });
    };

    const saveQuote = async ({ resetAfterSave = true } = {}) => {
        const est_time = moment()
            .tz("America/New_York")
            .format("MMMM DD, YYYY hh:mm:ss A z");

        const payload = {
            ...formData,
            est_timestamp: est_time
        };

        try {
            const response = await quoteMeService.saveQuoteData(payload);
            const data = response.data;

            if (data.status !== 'success') {
                setAlert({ type: 'error', message: data.message });
                throw new Error(data.message);
            }

            setAlert({ type: 'success', message: data.message });

            if (resetAfterSave) {
                // 👉 ONLY reset when clicking Save
                setFormData(prev => ({
                    ...prev,
                    carrier: null,
                    shipping_address: null,
                    ship_service: "",
                    date_schedule: "",
                    notes: "",
                    sub_total: "0.00",
                    sales_tax: "0.00",
                    total: "0.00",
                    sales_data: [{
                        type: "Sale",
                        product_number: "",
                        description: "",
                        unit_price: "0.00",
                        quantity: "",
                        total_price: "0.00"
                    }],
                }));

                setSameAsBilling(false);
                getNextQuoteOrderNumber();
            }

            return payload; // 👈 IMPORTANT for Share
        } catch (error) {
            setAlert({ type: 'error', message: error.message || 'Save failed' });
            throw error;
        } finally {
            setShowLoader(false);
        }
    };

    const normalizeQuoteForShare = (payload) => ({
        ...payload,
        order_date: moment(payload.order_date).format('MM/DD/YYYY'),
        date_schedule: moment(payload.date_schedule).format('MM/DD/YYYY'),
        sub_total: formatUSD(payload.sub_total || 0),
        sales_tax: formatUSD(payload.sales_tax || 0),
        total: formatUSD(payload.total || 0),
        sales_data: (payload.sales_data || []).map(item => ({
            ...item,
            unit_price: formatUSD(item.unit_price || 0),
            total_price: formatUSD(item.total_price || 0)
        }))
    });

    const handleShare = async (email, quote_note) => {

        if (!validateSalesRows()) {
            throw new Error("Please fix validation errors before sharing.");
        }

        const savedPayload = await saveQuote({ resetAfterSave: true });

        const sharePayload = ({
            ...savedPayload,
            email_to: [email],
            quote_note: quote_note, 
            format: 'both'
        });

        const payload = normalizeQuoteForShare(sharePayload);

        const response = await quoteMeService.shareQuote(payload);

        if (response.data.response !== 'success') {
            throw new Error(response.data.message || 'Share failed');
        }

        return true;
    };

    const exportPdf = () => {
        setShowLoader(true);

        quoteMeService.exportQuoteOrderPDF(formData)
            .then(response => {
                setShowLoader(false);

                // response.data IS already a Blob
                const blob = new Blob([response.data], {
                    type: 'application/pdf'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = `quote-${formData.order_number}.pdf`;

                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                setShowLoader(false);
                console.error('Error exporting PDF:', error);
            });
    };

    const renderDefaultData = () => {
		if (!selectedCustomer) {
			return (
                <div className="text-center my-5 fade-in">
                    <div className="p-5 rounded-3 shadow-sm bg-light border d-inline-block">
                    
                    <h3 className="fw-bold text-dark">No Customer Selected</h3>
                    <p className="text-muted mb-0">
                        Please <span className="fw-semibold">select a customer</span> from the dropdown above to view Quote me section.
                    </p>
                    </div>
                </div>
			);
		}
	};

    const renderNoProducts = () => {
        return (
            <div className="text-center my-5 fade-in">
                <div className="p-5 rounded-3 shadow-sm bg-light border d-inline-block">
                    <h3 className="fw-bold text-dark">No Products Found</h3>
                    <p className="text-muted mb-0">
                        This customer does not have any products or pricing configured.
                    </p>
                </div>
            </div>
        );
    };

    const renderSalesTable = () => {
        return(
            <React.Fragment>
                <div className="row">
                    <div className="col-md-12">
                        <div className="table-responsive">
                            <QuoteCustomerSalesTable
                                salesData={formData.sales_data}
                                setFormData={setFormData}
                                products={products}
                                productMap={productMap}
                                multiplierMap={multiplierMap} 
                                rowErrors={rowErrors}
                            />


                        </div>	
                        <div className="table-responsive mt-5">
                            <table className="table table-bordered techtop-quote-table2">
                                <tbody>
                                    <tr>
                                        <td rowSpan="3"><label>Notes:</label>
                                            <textarea className="form-control" id="notes" rows="2" placeholder="Notes" name="notes" value={formData.notes || ''} onChange={handleInputChange}></textarea>
                                        </td>
                                        <td className="text-end"><strong>Subtotal:</strong></td> 
                                        <td className="text-end"><strong>${formatUSD(formData.sub_total)}</strong></td>	
                                    </tr>
                                    <tr>
                                        <td className="text-end"><strong>Sales Tax:</strong></td>
                                        <td className="text-end"><strong>${formatUSD(formData.sales_tax)}</strong></td>	
                                    </tr>
                                    <tr>
                                        <td className="text-end"><strong>Total:</strong></td>
                                        <td className="text-end"><strong>${formatUSD(formData.total)}</strong></td>	
                                    </tr>
                                </tbody>
                            </table>
                        </div>	
                        <p className="dark-color1">{estTime}</p>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    const renderQuote = () => {
        return(
            <React.Fragment>
                <div className="row">
                    <AlertMessage
                        type={alert.type}
                        message={alert.message}
                        onClose={() => setAlert({ type: '', message: '' })}
                    />
                    <div className="col-md-10" id="printable">
                        <div className="searchdiv mb-3" id="customer-select-div">
                            <div className="row">
                                <div className="col-md-3 align-self-center">
                                    <h6 className="text-end m-0">Customer Name</h6>
                                </div>
                                <div className="col-md-9">	
                                    <Select
                                        classNamePrefix="react-select"
                                        placeholder="Search by Customer..."
                                        options={customers}
                                        value={selectedCustomer}
                                        isClearable
                                        onChange={(selected) => setSelectedCustomer(selected)}
                                    />
                                </div>
                            </div>
                        </div>	
                        {selectedCustomer && isProducts && (
                            <React.Fragment>
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="section-div1">
                                            <div className="row">
                                                <div className="col-md-8">
                                                    <img src={logo} alt="Techtop" />
                                                    <h3>Techtop Industries, Inc.</h3>
                                                    <p className="m-0 text-20">Techtop Industries, Inc.</p>
                                                    <p className="m-0 text-20">2815 Colonnades Court</p>
                                                    <p className="m-0 text-20">Peachtree Corners, GA 30071</p>
                                                </div>
                                                <div className="col-md-4">
                                                    <h3 className="text-center">Quote</h3>
                                                    <table className="table table-bordered quote-table text-center">
                                                        <thead>
                                                            <tr>
                                                                <th>Order #</th>
                                                                <th>Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td>{formData.order_number}</td>
                                                                <td>{moment().format('MM/DD/YYYY')}</td>
                                                            </tr>
                                                        </tbody>	
                                                    </table>	
                                                </div>
                                            </div>	
                                        </div>
                                    </div>
                                </div>	
                                <div className="row">
                                    <div className="col-md-6">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Bill To:</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <textarea className="form-control" id="billing_address" name="billing_address" rows="2" placeholder="Billing Address" value={formData.billing_address || ''} onChange={handleInputChange}></textarea>
                                                    </td>
                                                </tr>
                                            </tbody>	
                                        </table>	
                                        <table className="table table-bordered equal-table">
                                            <tbody>
                                                <tr>
                                                    <td>Customer: {formData.customer_name}</td>
                                                </tr>
                                            </tbody>
                                        </table>	
                                    </div>
                                    <div className="col-md-6">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th style={{ borderRight: "0" }}>Ship To: US</th>
                                                    <th style={{ borderLeft: "0" }}>
                                                        <div className="form-check techtop-div-check">	
                                                            <input className="form-check-input" type="checkbox" name="same_billing" id="same_billing" checked={sameAsBilling} onChange={handleSameAsBilling}/>
                                                            <label className="form-check-label" htmlFor="same_billing">Same</label>
                                                        </div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td colSpan="2">
                                                        <textarea className="form-control" id="shipping_address" rows="2" placeholder="Entry" name="shipping_address" value={formData.shipping_address || ''} onChange={handleInputChange}></textarea>
                                                    </td>
                                                </tr>
                                            </tbody>	
                                        </table>
                                        <table className="table table-bordered equal-table">
                                            <tbody>
                                                <tr>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>		
                                    </div>
                                </div>	
                                
                                <div className="row">
                                    <div className="col-md-12">
                                        <div className="table-responsive">
                                            <table className="table table-bordered techtop-quote-table text-center">
                                                <thead>
                                                    <tr>
                                                        <th>Sales Rep</th>
                                                        <th>Payment Terms</th>
                                                        <th>FOB Point</th>
                                                        <th style={{width:"15%"}}>Carrier</th>
                                                        <th>Ship Service</th>
                                                        <th>Date Scheduled</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>{formData.rep}</td>
                                                        <td>{formData.payment_terms}</td>
                                                        <td>{formData.fob_point}</td>
                                                        <td>
                                                            <select className="form-select" aria-label="Default select example" name="carrier" id="carrier" value={formData.carrier} onChange={handleInputChange}>
                                                                <option selected>Select Carrier</option>
                                                                <option value="UPS">UPS</option>
                                                                <option value="Will Call">Will Call</option>
                                                                <option value="LTL">LTL</option>
                                                                <option value="Courier">Courier</option>
                                                                <option value="3rd Part Billing">3rd Part Billing</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input type="text" className="form-control" placeholder="Ship Service" name="ship_service" id="ship_service" value={formData.ship_service} onChange={handleInputChange}/>
                                                        </td>	
                                                        <td><input type="date" className="form-control" name="date_schedule" id="date_schedule" value={formData.date_schedule} onChange={handleInputChange}/></td>
                                                    </tr>													
                                                </tbody>
                                            </table>
                                        </div>	
                                    </div>
                                </div>

                                {renderSalesTable()}
                            </React.Fragment>
                        )}
                                
                        
                    </div>
                    {!showSpinner && renderDefaultData()}
                    {!showSpinner && selectedCustomer && !isProducts && renderNoProducts()}
                    {selectedCustomer && isProducts && (
                        <React.Fragment>
                            <div className="col-md-2">
                                <ul className="net-pricing-report-list">
                                    <li><button className="btn-link-style" onClick={() => handleSubmit()}><img src={saveIcon} alt="Save" /> Save</button></li> 
                                    {/* <li><button className="btn-link-style" onClick={() => window.print()}><img src={printIcon} alt="Print" /> Print</button></li> */}
                                    <li><button className="btn-link-style" onClick={() => handleShareClick()}><img src={shareIcon} alt="Share" /> Share</button></li> 
                                    {/* <li><button className="btn-link-style" onClick={() => exportPdf()}><img src={pdfIcon} alt="PDF" />Export PDF</button></li>  */}
                                </ul>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </React.Fragment>
        )
    } 

    return (
        <React.Fragment>
            <div className="container tab-pane active">
                {renderQuote()}
                {showSpinner && <GearSpinner />}
                <Loader isLoading={showLoader} message="Processing..."/>
                <ShareQuoteMeModal 
					isShow={showShareModal} 
                    payload={formData}
					onClose={() => setShowShareModal(false)} 
                    onShare={handleShare}
				/>
            </div>
        </React.Fragment>
    )

}

export default QuoteCustomerTab
